import dayjs from "dayjs";
import { isEquitySellType } from "../../../utils/reports/equityTransactionTypes";
import { formatReportTypeColumnLabel } from "../../../utils/reports/reportDisplayLabels";
import { withTxnBenchmarkFields } from "../../../utils/reports/transactionBenchmarkColumns";
import { withTxnMetadataFields } from "../../../utils/reports/transactionMetadataColumns";
import {
  attachInvestorMeta,
  buildUserMetaFromInvestors,
  type UserMeta,
} from "../mf/mfReportParsing";
import { BOND_DUE_DATE_REPORT } from "./bondReportConfig";

export { attachInvestorMeta, buildUserMetaFromInvestors, type UserMeta };

const formatEpochDate = (value: unknown): string => {
  if (value == null || value === "" || value === "-") return "-";
  const n = Number(value);
  if (Number.isFinite(n) && n > 0) {
    return dayjs(n).format("DD/MM/YYYY");
  }
  return String(value);
};

const toReportNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "" || value === "-") {
    return null;
  }
  const n =
    typeof value === "number" ? value : Number(String(value).replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
};

/** Table formatters accept epoch or DD/MM/YYYY; keep raw values (same as equity MIS). */
const resolveCurrentDateOrToday = (row: Record<string, any>): unknown =>
  row.currentDate ?? row.current_date ?? Date.now();

const getBondSchemeName = (row: Record<string, any>): string =>
  String(row.promoter ?? row.schemeName ?? row.name ?? "-");

const getHoldingTransactionList = (holding: Record<string, any>): any[] => {
  const list =
    holding.transactions ??
    holding.transaction ??
    holding.txns ??
    holding.trxn ??
    [];
  return Array.isArray(list) ? list : [];
};

const isSellTxn = (row: Record<string, any>): boolean => {
  const raw = String(
    row.transType ?? row.txnType ?? row.transactionType ?? row.transactiontype ?? "",
  ).trim();
  if (!raw) return false;
  return isEquitySellType(raw) || raw.toUpperCase() === "SELL";
};

const isBuyTxn = (row: Record<string, any>): boolean => {
  const raw = String(
    row.transType ?? row.txnType ?? row.transactionType ?? row.transactiontype ?? "",
  )
    .trim()
    .toUpperCase();
  return raw === "BUY" || raw === "PURCHASE";
};

const netUnitsFromTransactions = (holding: Record<string, any>): number | null => {
  const list = getHoldingTransactionList(holding);
  if (!list.length) return null;
  let net = 0;
  let sawQty = false;
  for (const txn of list) {
    const qty = toReportNumber(txn?.units ?? txn?.quantity);
    if (qty === null) continue;
    sawQty = true;
    net += isSellTxn(txn) ? -qty : qty;
  }
  return sawQty ? net : null;
};

/**
 * aumsampaan `filterHoldingsWithPositiveUnitsOrQuantity`, plus net units from
 * nested txns when stored holding.units is stale (0 after a full sell in DB
 * while the selected date range still has a long position).
 */
const filterUnrealizedBondHoldings = (rows: any[]): any[] => {
  if (!Array.isArray(rows) || rows.length === 0) return rows;
  return rows.filter((row) => {
    if (!row || typeof row !== "object") return true;
    const net = netUnitsFromTransactions(row);
    const stored = toReportNumber(
      row.units ?? row.quantity ?? row.qty ?? row.closingBalance,
    );
    const units =
      (stored === null || stored === 0) && net != null && net > 0
        ? net
        : stored;
    if (units === null) return true;
    if (units > 0) return true;
    const invested = toReportNumber(
      row.investedAmount ?? row.costValue ?? row.invested_amount ?? row.amount,
    );
    const current = toReportNumber(
      row.currentAmount ?? row.currentValue ?? row.marketValue,
    );
    return (
      (invested !== null && invested > 0) || (current !== null && current > 0)
    );
  });
};

const filterRealizedTxnRows = (rows: any[]) =>
  rows.filter((r) => isSellTxn(r)).map((r) => ({
    ...r,
    type: formatReportTypeColumnLabel("Realized", "Realized"),
    displayTransType: "Realized",
  }));

const resolveHoldingCagr = (row: Record<string, any>): string | number => {
  const meta = row?.metadata ?? row?.meta ?? row?.metaData ?? {};
  for (const key of ["cagr", "cagrBond", "yield", "roi"] as const) {
    const val = row[key] ?? meta[key];
    if (val != null && val !== "") return val;
  }
  return row.cagr ?? "-";
};

const pickFirstBuyTxn = (holding: Record<string, any>): Record<string, any> | null => {
  const buys = getHoldingTransactionList(holding).filter(isBuyTxn);
  if (!buys.length) return null;
  return [...buys].sort(
    (a, b) =>
      Number(a.transactionDate ?? a.transactiondate ?? 0) -
      Number(b.transactionDate ?? b.transactiondate ?? 0),
  )[0];
};

const summarizeOpenBondLots = (holding: Record<string, any>) => {
  const txns = [...getHoldingTransactionList(holding)].sort(
    (a, b) =>
      Number(a.transactionDate ?? a.transactiondate ?? 0) -
      Number(b.transactionDate ?? b.transactiondate ?? 0),
  );
  type Lot = {
    units: number;
    amount: number;
    currentValue: number;
    gainLoss: number;
    gainLossPercentage: number | null;
  };
  const lots: Lot[] = [];
  for (const txn of txns) {
    const qty = toReportNumber(txn.units ?? txn.quantity) ?? 0;
    if (qty <= 0) continue;
    if (isSellTxn(txn)) {
      let remaining = qty;
      while (remaining > 0 && lots.length) {
        const lot = lots[0];
        const take = Math.min(lot.units, remaining);
        const ratio = lot.units > 0 ? take / lot.units : 0;
        lot.units -= take;
        lot.amount -= lot.amount * ratio;
        lot.currentValue -= lot.currentValue * ratio;
        lot.gainLoss -= lot.gainLoss * ratio;
        remaining -= take;
        if (lot.units <= 1e-8) lots.shift();
      }
      continue;
    }
    const meta = txn.metaData ?? txn.metadata ?? txn.meta ?? {};
    lots.push({
      units: qty,
      amount: toReportNumber(txn.amount ?? txn.costValue) ?? 0,
      currentValue:
        toReportNumber(txn.currentAmount ?? txn.currentValue ?? meta.currentValue) ??
        0,
      gainLoss:
        toReportNumber(
          txn.unrealisedGainLoss ?? txn.unrealisedPNL ?? txn.gainLoss,
        ) ?? 0,
      gainLossPercentage: toReportNumber(
        txn.unrealisedGainLossPercentage ??
          txn.unrealisedPNLPercentage ??
          txn.gainLossPercentage,
      ),
    });
  }
  const units = lots.reduce((sum, lot) => sum + lot.units, 0);
  const investedAmount = lots.reduce((sum, lot) => sum + lot.amount, 0);
  const currentAmount = lots.reduce((sum, lot) => sum + lot.currentValue, 0);
  const gainLoss = lots.reduce((sum, lot) => sum + lot.gainLoss, 0);
  return {
    units,
    investedAmount,
    currentAmount,
    gainLoss,
    gainLossPercentage:
      investedAmount !== 0 ? (gainLoss / investedAmount) * 100 : null,
  };
};

const resolveHoldingAverageDays = (row: Record<string, any>): string | number => {
  const meta = row?.metadata ?? row?.meta ?? {};
  const firstBuy = pickFirstBuyTxn(row);
  const firstBuyMeta = firstBuy?.metaData ?? firstBuy?.metadata ?? firstBuy?.meta ?? {};
  const val =
    row.averageDays ??
    row.averageDay ??
    row.days ??
    meta.averageDays ??
    meta.days ??
    firstBuy?.days ??
    firstBuyMeta.days;
  if (val == null || val === "") return "-";
  return val;
};

const normalizeBondTotalHoldingInfo = (
  info: Record<string, unknown> | null | undefined,
): Record<string, unknown> | null => {
  if (!info || typeof info !== "object" || Object.keys(info).length === 0) {
    return null;
  }
  return {
    ...info,
    totalInvested:
      info.totalInvested ?? info.totalValueInvested ?? info.investedAmount,
    totalValueInvested:
      info.totalValueInvested ?? info.totalInvested ?? info.investedAmount,
    currentValue: info.currentValue ?? info.currentAmount,
    gainLoss: info.gainLoss ?? info.unrealisedGainLoss,
    unrealisedGainLoss: info.unrealisedGainLoss ?? info.gainLoss,
    gainLossPercentage: info.gainLossPercentage ?? info.roi,
    roi: info.roi ?? info.gainLossPercentage,
    cagrBond: info.cagrBond ?? info.cagr,
    cagr: info.cagr ?? info.cagrBond,
    xirr: info.xirr,
  };
};

const stripBondHoldingPayloadNoise = (row: Record<string, any>) => {
  const {
    payoutSchedule,
    transactions,
    transaction,
    txns,
    trxn,
    ...rest
  } = row;
  return rest;
};

const normalizeUnrealizedHoldingRow = (row: Record<string, any>) => {
  const meta = row?.metadata ?? row?.meta ?? {};
  const schemeName = getBondSchemeName(row);
  const firstBuy = pickFirstBuyTxn(row);
  const firstBuyMeta = firstBuy?.metaData ?? firstBuy?.metadata ?? firstBuy?.meta ?? {};
  const openLots = summarizeOpenBondLots(row);
  const storedUnits = toReportNumber(row.units ?? row.quantity) ?? 0;
  const storedInvested = toReportNumber(
    row.investedAmount ?? row.amount ?? row.costValue,
  );
  const storedCurrent = toReportNumber(
    row.currentAmount ?? row.currentValue ?? row.marketValue,
  );
  const useOpenLots =
    openLots.units > 0 &&
    storedUnits <= 0 &&
    !(storedInvested !== null && storedInvested > 0) &&
    !(storedCurrent !== null && storedCurrent > 0);
  const purchaseDate =
    row.purchaseDate ??
    row.transactionDate ??
    meta.purchaseDate ??
    firstBuy?.transactionDate ??
    firstBuy?.transactiondate;
  const currentDateSource = {
    currentDate:
      row.currentDate ??
      row.current_date ??
      firstBuy?.currentDate ??
      firstBuyMeta.currentDate,
  };
  return withTxnMetadataFields(
    withTxnBenchmarkFields({
      ...stripBondHoldingPayloadNoise(row),
      schemeName,
      promoter: row.promoter ?? schemeName,
      type: formatReportTypeColumnLabel("Unrealized", "Unrealized"),
      purchaseDate,
      currentDate: resolveCurrentDateOrToday(currentDateSource),
      investedAmount: useOpenLots
        ? openLots.investedAmount
        : (row.investedAmount ?? row.amount ?? row.costValue),
      currentAmount: useOpenLots
        ? openLots.currentAmount
        : (row.currentAmount ?? row.currentValue ?? row.marketValue),
      gainLoss: useOpenLots
        ? openLots.gainLoss
        : (row.gainLoss ??
          row.unrealisedGainLoss ??
          row.unrealisedPNL ??
          row.unrealizedPNL),
      gainLossPercentage: useOpenLots
        ? openLots.gainLossPercentage
        : (row.gainLossPercentage ??
          row.unrealisedGainLossPercentage ??
          row.unrealisedPNLPercentage),
      cagr: resolveHoldingCagr(row),
      days: resolveHoldingAverageDays(row),
      currentValue:
        row.currentValue ??
        (useOpenLots
          ? openLots.currentAmount
          : (row.currentAmount ?? row.currentValue ?? row.marketValue)),
      costValue:
        row.costValue ??
        (useOpenLots
          ? openLots.investedAmount
          : (row.investedAmount ?? row.amount ?? row.costValue)),
      adjustedCostPrice:
        row.adjustedCostPrice ?? meta.adjustedCostPrice ?? row.adjCost,
      isin: row.isin ?? "-",
      units: useOpenLots ? openLots.units : row.units,
    }),
  );
};

const normalizeRealizedRow = (row: Record<string, any>) => {
  const meta = row.metadata ?? row.meta ?? {};
  const schemeName = getBondSchemeName(row);
  const avgBuy =
    row.avgBuyPrice ??
    row.averageBuyPrice ??
    row.price ??
    row.averagePrice ??
    meta.avgBuyPrice;
  const sellPrice = row.currentPrice ?? row.price ?? row.purchaseNAV;
  return withTxnMetadataFields(
    withTxnBenchmarkFields({
      ...row,
      schemeName,
      promoter: row.promoter ?? schemeName,
      type: formatReportTypeColumnLabel("Realized", "Realized"),
      transactionDate: formatEpochDate(
        row.transactionDate ?? row.transactiondate ?? row.date,
      ),
      purchaseDate: formatEpochDate(row.purchaseDate ?? meta.purchaseDate),
      currentDate: resolveCurrentDateOrToday(row),
      settlementDate: formatEpochDate(row.settlementDate),
      maturityDate: formatEpochDate(row.maturityDate),
      purchaseNAV: sellPrice ?? row.purchaseNAV ?? "-",
      avgBuyPrice: avgBuy ?? "-",
      saleValue: row.saleValue ?? row.amount ?? row.currentAmount,
      units: row.units ?? row.quantity,
      realisedPnl:
        row.realisedPnl ??
        row.realisedPnL ??
        row.realisedPNL ??
        row.realizedPnl ??
        row.gainLoss,
      realisedPNLPercentage:
        row.realisedPNLPercentage ??
        row.realisedPnLPercentage ??
        row.realizedPNLPercentage ??
        row.gainLossPercentage,
      isin: row.isin ?? meta.isin ?? "-",
      cagr: resolveHoldingCagr(row),
    }),
  );
};

const flattenBondHoldingsTransactions = (holdings: any[]): any[] => {
  const txns: any[] = [];
  for (const h of holdings || []) {
    if (!h || typeof h !== "object") continue;
    const schemeName = getBondSchemeName(h);
    const list = getHoldingTransactionList(h);
    if (!list.length) continue;
    for (const t of list) {
      txns.push(
        withTxnMetadataFields(
          withTxnBenchmarkFields({
            ...t,
            schemeName: t.schemeName ?? schemeName,
            promoter: t.promoter ?? h.promoter ?? schemeName,
            isin: t.isin ?? h.isin,
            userId: t.userId ?? h.userId,
            memberName: t.memberName ?? h.memberName,
            transactionDate: formatEpochDate(
              t.transactionDate ?? t.transactiondate ?? t.date,
            ),
            settlementDate: formatEpochDate(t.settlementDate),
            maturityDate: formatEpochDate(t.maturityDate),
            currentDate: resolveCurrentDateOrToday({
              currentDate: t.currentDate ?? t.current_date ?? h.currentDate,
            }),
            transType: t.transType ?? t.txnType ?? t.transactionType,
            price: t.price ?? t.purchasePrice,
            currentPrice: t.currentPrice ?? t.currentNAV ?? h.currentPrice,
            amount: t.amount ?? t.costValue,
            currentAmount: t.currentAmount ?? t.currentValue,
            units: t.units ?? t.quantity,
            unrealisedGainLoss:
              t.unrealisedGainLoss ??
              t.unrealisedPNL ??
              t.gainLoss,
            unrealisedGainLossPercentage:
              t.unrealisedGainLossPercentage ??
              t.unrealisedPNLPercentage ??
              t.gainLossPercentage,
            days: t.days ?? t.avgDays ?? h.averageDays,
          }),
        ),
      );
    }
  }
  return txns;
};

const normalizeHoldingRowForAll = (row: Record<string, any>) => {
  const normalized = normalizeUnrealizedHoldingRow({
    ...row,
    type: "Unrealized",
  });
  return {
    ...normalized,
    averageNav: normalized.price ?? normalized.averageNav,
    currentNAV: normalized.currentPrice ?? normalized.currentNAV,
    costValue:
      normalized.investedAmount ?? normalized.costValue ?? normalized.amount,
    currentValue:
      normalized.currentAmount ?? normalized.currentValue ?? normalized.marketValue,
    gainLoss: normalized.gainLoss,
    gainLossPercentage: normalized.gainLossPercentage,
  };
};

const normalizeTxnRowForAll = (row: Record<string, any>) => {
  const isSell = isSellTxn(row);
  const schemeName = getBondSchemeName(row);
  return {
    ...normalizeRealizedRow({ ...row, schemeName }),
    schemeName,
    type: formatReportTypeColumnLabel(
      isSell ? "Realized" : "Unrealized",
      isSell ? "Realized" : "Unrealized",
    ),
    costValue: row.amount ?? row.costValue,
    currentValue: row.currentAmount ?? row.currentValue,
  };
};

const extractBondHoldings = (payload: any): any[] => {
  if (Array.isArray(payload?.holdings)) return payload.holdings;
  if (Array.isArray(payload?.data?.holdings)) return payload.data.holdings;
  if (Array.isArray(payload?.data?.data?.holdings)) {
    return payload.data.data.holdings;
  }
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

const extractTotalHoldingInfo = (payload: any): Record<string, unknown> | null =>
  normalizeBondTotalHoldingInfo(
    payload?.totalHoldingInfo ??
      payload?.totalHolding ??
      payload?.data?.totalHoldingInfo ??
      null,
  );

export type ParsedBondReport = {
  rows: any[];
  totalHoldingInfo?: Record<string, unknown> | null;
  totalPages?: number;
  pageIndex?: number;
  totalRecords?: number;
};

export const parseBondReportResponse = (
  reportType: string,
  payload: any,
  userMeta: UserMeta,
  selectedUserIds: number[] = [],
): ParsedBondReport => {
  let rows: any[] = [];
  let totalHoldingInfo: Record<string, unknown> | null = null;
  let totalPages = 1;
  let pageIndex = 1;
  let totalRecords = 0;

  switch (reportType) {
    case "Holding": {
      const holdings = extractBondHoldings(payload);
      // Bond getHoldings already skips holdings with no txns. Do not drop
      // units===0 rows — DB units stay 0 after a full sell, which hid Unrealized.
      rows = holdings.map(normalizeUnrealizedHoldingRow);
      totalHoldingInfo = extractTotalHoldingInfo(payload);
      totalPages = Number(payload?.totalPages) || 1;
      pageIndex = Number(payload?.pageIndex) || 1;
      totalRecords = Number(payload?.totalRecords) || rows.length;
      break;
    }
    case BOND_DUE_DATE_REPORT: {
      const holdings = extractBondHoldings(payload);
      rows = filterUnrealizedBondHoldings(holdings).map(
        normalizeUnrealizedHoldingRow,
      );
      totalHoldingInfo = extractTotalHoldingInfo(payload);
      totalPages = Number(payload?.totalPages) || 1;
      pageIndex = Number(payload?.pageIndex) || 1;
      totalRecords = Number(payload?.totalRecords) || rows.length;
      break;
    }
    case "Realized": {
      const holdings = extractBondHoldings(payload);
      rows = filterRealizedTxnRows(flattenBondHoldingsTransactions(holdings)).map(
        normalizeRealizedRow,
      );
      totalHoldingInfo = extractTotalHoldingInfo(payload);
      totalPages = Number(payload?.totalPages) || 1;
      pageIndex = Number(payload?.pageIndex) || 1;
      totalRecords = rows.length;
      break;
    }
    case "All": {
      const holdings = extractBondHoldings(payload);
      totalHoldingInfo = extractTotalHoldingInfo(payload);
      const unrealized = filterUnrealizedBondHoldings(holdings).map(
        normalizeHoldingRowForAll,
      );
      const realized = filterRealizedTxnRows(
        flattenBondHoldingsTransactions(holdings),
      ).map(normalizeTxnRowForAll);
      rows = [...unrealized, ...realized];
      totalPages = Number(payload?.totalPages) || 1;
      pageIndex = Number(payload?.pageIndex) || 1;
      totalRecords = rows.length;
      break;
    }
    case "Transaction": {
      const holdings = extractBondHoldings(payload);
      totalHoldingInfo = extractTotalHoldingInfo(payload);
      rows = flattenBondHoldingsTransactions(holdings);
      totalPages = Number(payload?.totalPages) || 1;
      pageIndex = Number(payload?.pageIndex) || 1;
      totalRecords = rows.length;
      break;
    }
    default:
      rows = [];
  }

  return {
    rows: attachInvestorMeta(rows, userMeta, selectedUserIds),
    totalHoldingInfo,
    totalPages,
    pageIndex,
    totalRecords,
  };
};

export const mergeBondTransactionPages = (existing: any[], next: any[]) => {
  const seen = new Set(
    existing.map(
      (r) =>
        `${r.userId}-${r.schemeName ?? r.promoter}-${r.transactionDate}-${r.units ?? r.quantity}-${r.transType}`,
    ),
  );
  const merged = [...existing];
  for (const row of next) {
    const key = `${row.userId}-${row.schemeName ?? row.promoter}-${row.transactionDate}-${row.units ?? row.quantity}-${row.transType}`;
    if (!seen.has(key)) {
      seen.add(key);
      merged.push(row);
    }
  }
  return merged;
};
